import { Component, Inject, Injector, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  TransitionGuard,
  WorkflowEditorStore,
  FormField,
} from '@fry/booking/workflow-editor/lib';

export interface WorkflowTransitionGuardFormOptions {
  item: TransitionGuard|undefined;
  templates: TransitionGuard[];
  callerInjector?: Injector;
}

export type WorkflowTransitionGuardFormResult = TransitionGuard|string|undefined;

@Component({
    selector: 'app-workflow-transition-guard-form',
    templateUrl: './guard-form.component.html',
    styleUrls: ['./guard-form.component.css'],
    standalone: false
})
export class WorkflowTransitionGuardFormComponent implements OnDestroy {

  public readonly form: FormGroup;

  public  readonly original?: TransitionGuard;
  public           result?: TransitionGuard;
  public  readonly templates: TransitionGuard[];
  private readonly dialogRef: MatDialogRef<WorkflowTransitionGuardFormOptions, WorkflowTransitionGuardFormResult>;

  public availableFormFields: FormField[] = [];

  private destroyed$ = new Subject<void>();

  constructor(fb: FormBuilder,
              @Inject(MAT_DIALOG_DATA) data: WorkflowTransitionGuardFormOptions,
              dialogRef: MatDialogRef<WorkflowTransitionGuardFormOptions, WorkflowTransitionGuardFormResult>) {

    const workflowEditorStore = data.callerInjector?.get(WorkflowEditorStore);
    this.availableFormFields = workflowEditorStore?.workflow?.form?.fields ?? [];

    this.dialogRef = dialogRef;

    this.templates = data.templates;
    this.original = data.item;
    this.result = this.original ?? undefined;

    this.form = fb.group({
      template: [{ value: this.result?.type, disabled: !this.isAdding },
                 Validators.required],
    });
    if (this.result) {
      this.updateFormOptionsSection(this.result, fb);
    }

    // Build result after each update
    this.form.valueChanges
             .pipe(takeUntil(this.destroyed$))
             .subscribe(value => {
      if (this.isAdding) {
        const template = this.templates.find(t => t.type === this.form.value.template);
        if (!template) { return; }
        this.result = template.evalueToGuard(this.result?.id, undefined);
      }

      if (this.result?.hasOptions) {
        // We build new instance based on the template, reusing the ID and setting
        // the options data for the guard.
        let data = {};
        if (value.options !== undefined) {
          data = {
            field: value.options.target,
            op: value.options.operator,
            value: value.options.value
          };
        }
        this.result = this.result?.evalueToGuard(this.result?.id, data);
        this.updateFormOptionsSection(this.result, fb);
      }
    });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  // Build/Remove options section of the form if we need to
  private updateFormOptionsSection(guard: TransitionGuard, fb: FormBuilder): void {
    if (guard.options === undefined) {
      this.form.removeControl('options');
      return;
    }

    if (this.form.get('options') !== null) { return; }

    const value = {
      target: guard.data?.field ?? '',
      operator: guard.data?.op ?? '',
      value: guard.data?.value ?? ''
    };
    this.form.addControl('options', fb.control(value, Validators.required));
  }

  //
  // Actions
  //

  public onSave(): void {
    this.dialogRef.close(this.result);
  }

  public onCancel(): void {
    this.dialogRef.close(undefined);
  }

  public onDelete(): void {
    if (!this.result) { return; }
    this.dialogRef.close(this.result.id);
  }

  //
  // Template helpers
  //

  public get isAdding(): boolean {
    return !this.original;
  }

  public get shouldShowOptions(): boolean {
    return this.result !== undefined;
  }

  public get shouldShowDelete(): boolean {
    return !this.isAdding;
  }

  public get shouldShowSave(): boolean {
    return this.isAdding || (!this.isAdding && (this.result?.hasOptions ?? false));
  }

  getDialogTitle() {
    return this.isAdding ? $localize `ADD NEW GUARD` : $localize `EDIT GUARD`;
  }

  getAddOrApply() {
    return this.isAdding ? $localize `Add` : `Apply`;
  }
}
